import Cookies from 'js-cookie'
import { create } from 'zustand'
import { AuthUser, CreatedType } from '../types/auth'
import { authService } from '../services/authService'

const ACCESS_TOKEN = 'access_token'
const REFRESH_TOKEN = 'refresh_token'

interface AuthState {
  auth: {
    user: AuthUser | null
    account_type: CreatedType | null
    setUser: (user: AuthUser | null) => void
    setAccountType: (account_type: CreatedType | null) => void
    accessToken: string
    refreshToken: string
    setTokens: (accessToken: string, refreshToken: string) => void
    resetTokens: () => void
    reset: () => void
    restoreUser: () => Promise<void>
  }
}

export const useAuthStore = create<AuthState>()((set) => {
  const accessToken = Cookies.get(ACCESS_TOKEN) || ''
  const refreshToken = Cookies.get(REFRESH_TOKEN) || ''

  return {
    auth: {
      user: null,
      account_type: null,
      setUser: (user) =>
        set((state) => ({ ...state, auth: { ...state.auth, user } })),
      setAccountType: (account_type) =>
        set((state) => ({ ...state, auth: { ...state.auth, account_type } })),
      accessToken,
      refreshToken,
      setTokens: (accessToken, refreshToken) =>
        set((state) => {
          Cookies.set(ACCESS_TOKEN, accessToken)
          Cookies.set(REFRESH_TOKEN, refreshToken)
          return {
            ...state,
            auth: {
              ...state.auth,
              accessToken,
              refreshToken,
            },
          }
        }),
      resetTokens: () =>
        set((state) => {
          Cookies.remove(ACCESS_TOKEN)
          Cookies.remove(REFRESH_TOKEN)
          return {
            ...state,
            auth: {
              ...state.auth,
              accessToken: '',
              refreshToken: '',
            },
          }
        }),
      reset: () =>
        set((state) => {
          Cookies.remove(ACCESS_TOKEN)
          Cookies.remove(REFRESH_TOKEN)
          return {
            ...state,
            auth: {
              ...state.auth,
              user: null,
              account_type: null,
              accessToken: '',
              refreshToken: '',
            },
          }
        }),
      restoreUser: async () => {
        const state = useAuthStore.getState()
        if (state.auth.accessToken && !state.auth.user) {
          try {
            const userData = await authService.getMe()
            set((state) => ({
              ...state,
              auth: { ...state.auth, user: userData }
            }))
          } catch (_error) {
            // Reset auth if getMe fails
            set((state) => ({
              ...state,
              auth: {
                ...state.auth,
                user: null,
                account_type: null,
                accessToken: '',
                refreshToken: '',
              },
            }))
          }
        }
      },
    },
  }
})

export const useAuth = () => useAuthStore((state) => state.auth)
